import React, {
    PureComponent,
} from 'react'
import {
    ConstrainMode, DefaultButton, DetailsListLayoutMode, DirectionalHint, Icon, IconButton, SelectionMode, ShimmeredDetailsList, Text,
    TextField,
    TooltipDelay,
    TooltipHost,
} from '@fluentui/react'
import history from 'helpers/history'
import { handleRenderColumn, isValidDate, getUpdatedList } from 'helpers/methods/common'
import Status from 'types/status'
import Document from 'requests/objects/document'
import InvalidEntityError from 'requests/errors/invalidEntityError'
import CancelRequestError from 'requests/errors/cancelRequestError'
import NotImplementedError from 'requests/errors/notImplementedError'
import UnauthorizedError from 'requests/errors/unauthorizedError'
import flattenObj from 'helpers/methods/flattenObj'
import Card from 'components/containers/card'
import styles from 'styles/pages/documents/index.module.scss'
import { NEW_PATH } from 'types/others'
import { tKey, tObj } from 'helpers/methods/translate'
import { Link } from 'react-router-dom'
import time, { getLocaleFromLang } from 'helpers/methods/time'
import EFileFolder from 'types/files/enums/fileFolder'
import HandleBlob from 'helpers/methods/blob'
import File from 'requests/objects/file'
import { adminProfiles, clientProfiles, userProfilesWithSuperAdmin } from 'types/users/profiles'
import parseJson from 'helpers/methods/parseJson'
import ECompany from 'types/companies/enums/company'
import { Columns } from 'react-bulma-components'
import FilteredVirtualCombobox from 'components/inputs/filteredVirtualCombobox'
import Pagination from 'components/pages/Pagination'
import DatePicker from 'components/inputs/datePicker'

const DateFilterMinDate = new Date(1970, 0, 1)
const DateFilterMaxDate = new Date(2100, 11, 31)

/** @debug {import("app").AppProps} */

/**
 * @augments {PureComponent<import("app").AppProps>} extends
 */
/**
 * @typedef {object} SearchParamsType
 * @property {string|null} name name
 * @property {number[]} clientIds clientIds
 * @property {string|null} typeOfDocument typeOfDocument
 * @property {Date|null} creationDate creationDate
 * @property {Date|null} creationDateEnd creationDateEnd
 * @property {Date|null} startDate startDate
 * @property {Date|null} startDateEnd startDateEnd
 * @property {Date|null} endDate endDate
 * @property {Date|null} endDateEnd endDateEnd
 * @property {string|null} orderBy orderBy
 */
export default class IndexDocuments extends PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            /** @type {Status} Current status of the component */
            status: Status.IDLE,
            /** @type {Document[]} Items found in API */
            items: [],
            /** @type {Document[]} Items found in API */
            iniItems: [],
            /** @type {import('@fluentui/react').IColumn[]} */
            documentColumns: [],
            /** @type {import('@fluentui/react').IColumn[]} */
            priceColumns: [],
            /** @type {string} Current PriceListName */
            currentPriceListName: null,
            /** @type {SearchParamsType} Params to search */
            searchParams: {
                name: null,
                clientIds: [],
                typeOfDocument: null,
                creationDate: null,
                creationDateEnd: null,
                startDate: null,
                startDateEnd: null,
                endDate: null,
                endDateEnd: null,
                orderBy: null,

            },
            /** pagination */
            pagination: {
                currentPage: 1,
                totalPages: 1,
                totalRecords: 0,
                recordsPerPage: 20,
            },
        }

        this.renderChevron = this.renderChevron.bind(this)

        // this.submitInput = React.createRef()
    }

    /**
     * @inheritdoc
     */
    componentDidMount() {
        this.init()
        this.search()
    }

    /**
     * @inheritdoc
     * @param {object} prevProps Previous Props
     */
    componentDidUpdate(prevProps, prevState) {
        const { searchParams, pagination: { currentPage } } = this.state
        const { lang, me } = this.props

        if (JSON.stringify(prevState.searchParams) !== JSON.stringify(searchParams)) {
            history.replace({ search: '' })
            this.syncSearchParamsInHistory()
        }

        if (prevState.searchParams.orderBy !== searchParams.orderBy)
            this.setColumns()

        if (prevState.pagination.currentPage !== currentPage)
            this.search()

        if (lang !== prevProps.lang)
            this.init()

        if (me.companyId !== prevProps.me.companyId)
            this.init()
        // this.init()

        // this.setupBreadcrumb()

        // this.setupCommandBar()

        // this.setState({
        //     columns: this.buildColumns(),
        // })
    }

    /**
     * @inheritdoc
     */
    componentWillUnmount() {
        this.documentsHandlerGetAll?.cancel()
        this.filesHandlerGetFile?.cancel()
    }

    setColumns() {
        const { lang } = this.props
        const locale = getLocaleFromLang(lang)
        const { searchParams } = this.state
        const [sortedColumn = '', sortDirection = 'desc'] = (searchParams.orderBy || '').split('-')
        const getNextSort = columnKey => (
            sortedColumn !== columnKey || sortDirection === 'desc'
                ? 'asc'
                : 'desc'
        )
        const documentColumns = [
            {
                key: 'name',
                name: this.tKey('wording'),
                fieldName: 'name',
                minWidth: 150,
                maxWidth: 200,
                isResizable: true,
                isSorted: sortedColumn === 'name',
                isSortedDescending: sortDirection === 'desc',
                onColumnClick: () => this.search(`name-${getNextSort('name')}`),
                // eslint-disable-next-line react/no-unstable-nested-components
                onRender: data => (<Text style={{ color: '#00375d', fontWeight: 'bold' }}>{data.name}</Text>),
            },
            {
                key: 'createdAt',
                name: this.tKey('createdAt'),
                fieldName: 'createdAt',
                minWidth: 75,
                maxWidth: 125,
                isResizable: true,
                isSorted: sortedColumn === 'createdAt',
                isSortedDescending: sortDirection === 'desc',
                onColumnClick: () => this.search(`createdAt-${getNextSort('createdAt')}`),
                onRender: data => (data.createdAt ? time(data.createdAt, { locale }).getCleanDate({ year: 'numeric', month: '2-digit', day: '2-digit' }) : ''),
            },
            {
                key: 'typeOfDocument',
                name: this.tKey('documentType'),
                fieldName: 'typeOfDocument',
                minWidth: 150,
                maxWidth: 200,
                isResizable: true,
                onRender: item => this.tObj(parseJson(item.typeOfDocument)),
            },
            {
                key: 'startDate',
                name: this.tKey('startDate'),
                fieldName: 'startDate',
                minWidth: 70,
                maxWidth: 100,
                isResizable: true,
                isSorted: sortedColumn === 'startDate',
                isSortedDescending: sortDirection === 'desc',
                onColumnClick: () => this.search(`startDate-${getNextSort('startDate')}`),
                onRender: data => (data.startDate ? time(data.startDate, { locale }).getCleanDate({ year: 'numeric', month: '2-digit', day: '2-digit' }) : ''),
            },
            {
                key: 'endDate',
                name: this.tKey('endDate'),
                fieldName: 'endDate',
                minWidth: 70,
                maxWidth: 100,
                isResizable: true,
                isSorted: sortedColumn === 'endDate',
                isSortedDescending: sortDirection === 'desc',
                onColumnClick: () => this.search(`endDate-${getNextSort('endDate')}`),
                onRender: data => (data.endDate ? time(data.endDate, { locale }).getCleanDate({ year: 'numeric', month: '2-digit', day: '2-digit' }) : ''),
            },
        ]
        /** @type {import('@fluentui/react').IColumn[]} */
        const priceColumns = [
            {
                key: 'priceListName',
                name: this.tKey('priceList'),
                fieldName: 'priceListName',
                minWidth: 100,
                maxWidth: 150,
                isResizable: true,
                // eslint-disable-next-line react/no-unstable-nested-components
                onRender: data => (data.isHeader ? (
                    <div className={styles['price-list']}>
                        <Text style={{ color: '#00375d', fontWeight: 'bold' }}>{data.priceListName}</Text>
                        {this.renderChevron(data.priceListName)}
                    </div>
                ) : ''),
            },
            {
                key: 'priceCode',
                name: this.tKey('tariffCode'),
                fieldName: 'priceCode',
                minWidth: 60,
                maxWidth: 110,
                isResizable: true,
                // eslint-disable-next-line react/no-unstable-nested-components
                onRender: data => (data.isRow ? (<Text>{data.priceCode}</Text>) : ''),
            },
            {
                key: 'name',
                name: this.tKey('name'),
                fieldName: 'name',
                minWidth: 70,
                maxWidth: 130,
                isResizable: true,
                // eslint-disable-next-line react/no-unstable-nested-components
                onRender: data => (data.isRow ? (<Text style={{ color: '#00375d', fontWeight: 'bold' }}>{data.name}</Text>) : ''),
            },
            {
                key: 'priceType',
                name: this.tKey('tariffType'),
                fieldName: 'priceType',
                minWidth: 150,
                maxWidth: 250,
                isResizable: true,
                // eslint-disable-next-line react/no-unstable-nested-components
                onRender: data => (data.isRow ? (<Text>{data.priceType}</Text>) : ''),
            },
            {
                key: 'startDate',
                name: this.tKey('startDate'),
                fieldName: 'startDate',
                minWidth: 70,
                maxWidth: 100,
                isResizable: true,
                onRender: data => (data.isRow && data.startDate
                    ? time(data.startDate, { locale }).getCleanDate({ year: 'numeric', month: '2-digit', day: '2-digit' }) : ''),
            },
            {
                key: 'endDate',
                name: this.tKey('endDate'),
                fieldName: 'endDate',
                minWidth: 70,
                maxWidth: 100,
                isResizable: true,
                onRender: data => (data.isRow && data.endDate
                    ? time(data.endDate, { locale }).getCleanDate({ year: 'numeric', month: '2-digit', day: '2-digit' }) : ''),

            },
            {
                key: 'fichiers',
                name: this.tKey('files'),
                fieldName: 'file',
                minWidth: 75,
                maxWidth: 125,
                isResizable: true,
                className: styles['is-white'],
                // eslint-disable-next-line react/no-unstable-nested-components
                onRender: data => (data.isHeader ? (
                    <>
                        <IconButton
                            iconProps={{ iconName: 'PDF' }}
                            title="PDF"
                            href={data.url}
                            target="_blank"
                            onClick={ev => ev.stopPropagation()}
                        />
                        <IconButton
                            iconProps={{ iconName: 'ExcelDocument' }}
                            title={this.tKey('excelFile')}
                            href={data.excelFileUrl}
                            target="_blank"
                            onClick={ev => ev.stopPropagation()}
                        />
                    </>
                ) : ''),
            },
        ]
        this.setState({
            documentColumns,
            priceColumns,
        })
    }

    /**
     * Setup breadcrumb elements
     */
    setupBreadcrumb() {
        const { setBreadcrumb } = this.props

        setBreadcrumb([
            { text: this.tKey('documents'), key: 'document', isCurrentItem: true },
        ])
    }

    /**
     * Setup commandbar elements
     */
    setupCommandBar() {
        const { setCommand, me } = this.props
        const commandItems = []

        if (!clientProfiles.includes(me.profileId))
            commandItems.push({
                key: 'new',
                text: this.tKey('new'),
                iconProps: { iconName: 'Add' },
                onClick: () => history.push(`/documents/${NEW_PATH}`),
            })

        setCommand(commandItems)
    }

    /**
     * Init Page
     */
    init() {
        const { setMessageBar, setCommand } = this.props

        setMessageBar({ isDisplayed: false })
        setCommand([])

        this.setColumns()
        this.setupBreadcrumb()
        this.setupCommandBar()

        if (!window.location.search) {
            this.syncSearchParamsInHistory()
        } else {
            const queryParams = new URLSearchParams(window.location.search)
            const creationDateTimestamp = Date.parse(queryParams.get('creationDate'))
            const creationDateEndTimestamp = Date.parse(queryParams.get('creationDateEnd'))
            const startDateTimestamp = Date.parse(queryParams.get('startDate'))
            const startDateEndTimestamp = Date.parse(queryParams.get('startDateEnd'))
            const endDateTimestamp = Date.parse(queryParams.get('endDate'))
            const endDateEndTimestamp = Date.parse(queryParams.get('endDateEnd'))

            this.setState({
                searchParams: {
                    name: queryParams.get('name') || null,
                    clientIds: queryParams.getAll('clientIds').map(Number),
                    typeOfDocument: queryParams.get('typeOfDocument') || null,
                    creationDate: !Number.isNaN(creationDateTimestamp) ? new Date(creationDateTimestamp) : null,
                    creationDateEnd: !Number.isNaN(creationDateEndTimestamp) ? new Date(creationDateEndTimestamp) : null,
                    startDate: !Number.isNaN(startDateTimestamp) ? new Date(startDateTimestamp) : null,
                    startDateEnd: !Number.isNaN(startDateEndTimestamp) ? new Date(startDateEndTimestamp) : null,
                    endDate: !Number.isNaN(endDateTimestamp) ? new Date(endDateTimestamp) : null,
                    endDateEnd: !Number.isNaN(endDateEndTimestamp) ? new Date(endDateEndTimestamp) : null,
                    orderBy: queryParams.get('orderBy') || null,
                },
            }, () => this.syncSearchParamsInHistory())
        }
    }

    /**
     * Search elements
     * @param {string} [orderBy] field used for sorting
     */
    search(orderBy = undefined) {
        const { searchParams } = this.state
        const nextOrderBy = orderBy ?? searchParams.orderBy
        this.setState({
            status: Status.PENDING,
            searchParams: {
                ...searchParams,
                orderBy: nextOrderBy,
            },
        }, async () => {
            const { documentsHandler, match } = this.props
            const { searchParams: nextSearchParams, pagination: { currentPage, recordsPerPage } } = this.state
            try {
                this.documentsHandlerGetAll?.cancel()
                this.documentsHandlerGetAll = documentsHandler.getAllFromPage(
                    { ...nextSearchParams, orderBy: nextOrderBy, page: match.params?.key },
                    currentPage,
                    recordsPerPage,
                )
                const res = await this.documentsHandlerGetAll.fetch()
                this.setState({
                    items: res.items.map(x => this.flattenObj(x)),
                    iniItems: res.items,
                    pagination: {
                        currentPage: res.items.length === 0 ? 1 : currentPage,
                        totalPages: res.totalPages,
                        totalRecords: res.totalRecords,
                        recordsPerPage,
                    },
                    status: Status.RESOLVED,
                })
            } catch (error) {
                switch (error?.constructor) {
                    case CancelRequestError:
                    case UnauthorizedError:
                    case InvalidEntityError: break
                    case NotImplementedError:
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                    default:
                        this.setState({
                            items: [],
                            status: Status.REJECTED,
                        })
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                }
            }
        })
    }

    // eslint-disable-next-line react/no-unused-class-component-methods
    syncSearchParamsInHistory() {
        const { searchParams } = this.state

        const queryParams = new URLSearchParams(window.location.search)

        Object.entries(searchParams).forEach(([key, value]) => {
            queryParams.delete(key)

            if (Array.isArray(value) && value.length)
                value.forEach(v => queryParams.append(key, v))
            else if (value !== null && value !== '')
                queryParams.set(key, value)
        })

        history.replace({
            search: queryParams.toString(),
        })
    }

    /**
     * Download a file
     * @param {File} file file
     */
    async downloadFile(file) {
        try {
            const { filesHandler } = this.props
            this.filesHandlerGetFile = filesHandler.getFile(EFileFolder.Document, file.fileId)
            const blob = await this.filesHandlerGetFile.fetch()
            HandleBlob.download(blob, file?.name)
        } catch (error) {
            switch (error?.constructor) {
                case CancelRequestError:
                case UnauthorizedError:
                case InvalidEntityError: break
                case NotImplementedError:
                default:
                    // eslint-disable-next-line no-console
                    console.error(error)
                    break
            }
        }
    }

    /**
     * Render chevron
     * @param {string} priceListName priceListName
     * @returns {JSX.Element} chevron
     */
    renderChevron(priceListName) {
        const { currentPriceListName } = this.state
        return (
            <Icon
                style={{
                    color: '#00375d',
                    fontWeight: 'bold',
                    paddingLeft: '10px',
                    marginBottom: '2px',
                }}
                iconName={currentPriceListName !== priceListName ? 'ChevronRight' : 'ChevronDown'}
            />
        )
    }

    /**
     * Render component
     * @returns {JSX.Element} Element
     */
    render() {
        const {
            status, items, documentColumns, iniItems, priceColumns, currentPriceListName, searchParams, pagination,
        } = this.state
        const { me, lang, param } = this.props
        return (
            <main className={styles.index}>
                {adminProfiles.includes(me?.profileId) && (
                    <>
                        <Card
                            className={styles.prices}
                            title={this.tKey('filters')}
                            iconName="FilterSettings"
                        >
                            <Columns>
                                <Columns.Column size="one-quarter">
                                    <TextField
                                        label={this.tKey('label')}
                                        placeholder={this.tKey('label')}
                                        value={searchParams.name || ''}
                                        onChange={(ev, val) => this.setState({
                                            searchParams: {
                                                ...searchParams,
                                                name: val,
                                            },
                                        })}
                                    />
                                </Columns.Column>
                                <Columns.Column size="one-quarter">
                                    <TextField
                                        label={this.tKey('documentType')}
                                        placeholder={this.tKey('documentType')}
                                        value={searchParams.typeOfDocument || ''}
                                        onChange={(_ev, value) => {
                                            this.setState({
                                                searchParams: {
                                                    ...searchParams,
                                                    typeOfDocument: value || null,
                                                },
                                            })
                                        }}
                                    />
                                </Columns.Column>
                                <Columns.Column size="one-quarter">
                                    <FilteredVirtualCombobox
                                        label={this.tKey('clients')}
                                        placeholder={this.tKey('select')}
                                        options={[
                                            { key: 'ALL', text: this.tKey('selectAll') },
                                            ...param.clients.map(x => ({
                                                ...x,
                                                text: this.tObj(parseJson(x.text)),
                                            })),
                                        ]}
                                        selectedKey={searchParams.clientIds}
                                        onChange={(_ev, option) => this.setState({
                                            searchParams: {
                                                ...searchParams,
                                                clientIds: (() => {
                                                    if (option.key === 'ALL') {
                                                        if (searchParams.clientIds.length > 0)
                                                            return []

                                                        return param.clients.map(x => x.key)
                                                    }

                                                    return this.getUpdatedList(searchParams.clientIds, option)
                                                })(),
                                            },
                                        })}
                                        multiSelect
                                    />
                                </Columns.Column>
                            </Columns>
                            <Columns>
                                <Columns.Column size="one-quarter">
                                    <div className={styles['date-filter']}>

                                        <DatePicker
                                            label={this.tKey('creationDate')}
                                            date={searchParams.creationDate}
                                            minDate={DateFilterMinDate}
                                            maxDate={DateFilterMaxDate}
                                            lang={lang}
                                            onChangeDate={date => {
                                                this.setState({
                                                    searchParams: {
                                                        ...searchParams,
                                                        creationDate: this.isValidDate(date) ? date : null,
                                                    },
                                                })
                                            }}
                                        />
                                        <DatePicker
                                            disabled={!searchParams.creationDate}
                                            date={searchParams.creationDateEnd}
                                            minDate={searchParams.creationDate || DateFilterMinDate}
                                            maxDate={DateFilterMaxDate}
                                            lang={lang}
                                            onChangeDate={date => {
                                                this.setState({
                                                    searchParams: {
                                                        ...searchParams,
                                                        creationDateEnd: this.isValidDate(date) ? date : null,
                                                    },
                                                })
                                            }}
                                        />
                                    </div>
                                </Columns.Column>
                                <Columns.Column size="one-quarter">
                                    <div className={styles['date-filter']}>

                                        <DatePicker
                                            label={this.tKey('startDate')}
                                            date={searchParams.startDate}
                                            minDate={DateFilterMinDate}
                                            maxDate={DateFilterMaxDate}
                                            lang={lang}
                                            onChangeDate={date => {
                                                this.setState({
                                                    searchParams: {
                                                        ...searchParams,
                                                        startDate: this.isValidDate(date) ? date : null,
                                                    },
                                                })
                                            }}
                                        />
                                        <DatePicker
                                            disabled={!searchParams.startDate}
                                            date={searchParams.startDateEnd}
                                            minDate={searchParams.startDate || DateFilterMinDate}
                                            maxDate={DateFilterMaxDate}
                                            lang={lang}
                                            onChangeDate={date => {
                                                this.setState({
                                                    searchParams: {
                                                        ...searchParams,
                                                        startDateEnd: this.isValidDate(date) ? date : null,
                                                    },
                                                })
                                            }}
                                        />
                                    </div>
                                </Columns.Column>
                                <Columns.Column size="one-quarter">
                                    <div className={styles['date-filter']}>

                                        <DatePicker
                                            label={this.tKey('endDate')}
                                            date={searchParams.endDate}
                                            minDate={DateFilterMinDate}
                                            maxDate={DateFilterMaxDate}
                                            lang={lang}
                                            onChangeDate={date => {
                                                this.setState({
                                                    searchParams: {
                                                        ...searchParams,
                                                        endDate: this.isValidDate(date) ? date : null,
                                                    },
                                                })
                                            }}
                                        />
                                        <DatePicker
                                            disabled={!searchParams.endDate}
                                            date={searchParams.endDateEnd}
                                            minDate={searchParams.endDate || DateFilterMinDate}
                                            maxDate={DateFilterMaxDate}
                                            lang={lang}
                                            onChangeDate={date => {
                                                this.setState({
                                                    searchParams: {
                                                        ...searchParams,
                                                        endDateEnd: this.isValidDate(date) ? date : null,
                                                    },
                                                })
                                            }}
                                        />
                                    </div>
                                </Columns.Column>
                                <Columns.Column size="one-quarter">
                                    <div
                                        className={styles['action-filter-buttons']}
                                    >
                                        <div>
                                            <DefaultButton
                                                text={this.tKey('research')}
                                                primary
                                                disabled={status === Status.PENDING}
                                                onClick={() => this.search()}
                                                iconProps={{ iconName: 'Search' }}
                                            />
                                        </div>
                                        <div className={styles.separator} />
                                        <div>
                                            <TooltipHost
                                                content={this.tKey('resetFilters')}
                                                directionalHint={DirectionalHint.topAutoEdge}
                                                delay={TooltipDelay.medium}
                                                componentRef={this.toolTipRef}
                                            >
                                                <DefaultButton
                                                    disabled={status === Status.PENDING}
                                                    styles={{
                                                        root: {
                                                            minWidth: '40px',
                                                            maxWidth: '40px',
                                                        },
                                                    }}
                                                    iconProps={{ iconName: 'ClearFilter' }}
                                                    onClick={() => this.setState({
                                                        /** @type {SearchParamsType} */
                                                        searchParams: {
                                                            name: null,
                                                            clientIds: [],
                                                            typeOfDocument: null,
                                                            creationDate: null,
                                                            creationDateEnd: null,
                                                            startDate: null,
                                                            startDateEnd: null,
                                                            endDate: null,
                                                            endDateEnd: null,
                                                        },
                                                        pagination: {
                                                            ...pagination,
                                                            currentPage: 1,
                                                        },
                                                    },
                                                    () => this.search())}
                                                />
                                            </TooltipHost>
                                        </div>
                                    </div>
                                </Columns.Column>
                            </Columns>
                        </Card>
                        <br />
                    </>
                )}

                {clientProfiles.includes(me?.profileId) && (
                    <>
                        <Card
                            className={styles.prices}
                            title={this.tKey('prices')}
                            iconName="TextDocument"
                        >
                            <ShimmeredDetailsList
                                className={styles.prices}
                                items={items
                                    .filter(x => x.documentTypeId === 2)
                                    .reduce((acc, doc) => {
                                        const res = [...acc]
                                        if (!res.find(x => x.priceListName === doc.priceListName))
                                            res.push({ ...doc, isHeader: true })
                                        if (currentPriceListName === doc.priceListName)
                                            res.push({ ...doc, isRow: true })
                                        return res
                                    }, [])}
                                columns={priceColumns}
                                selectionMode={SelectionMode.none}
                                onShouldVirtualize={() => true}
                                enableShimmer={status === Status.PENDING}
                                layoutMode={DetailsListLayoutMode.justified}
                                constrainMode={ConstrainMode.unconstrained}
                                onRenderDetailsHeader={(props, defaultRender) => defaultRender({ ...props, styles: { root: { paddingTop: 0 } } })}
                                // eslint-disable-next-line react/no-unstable-nested-components
                                onRenderRow={(props, defaultRender) => {
                                    if (props.item.isHeader)
                                        return (
                                            <div className={styles['is-grey']}>
                                                <button
                                                    onClick={() => this.setState({
                                                        currentPriceListName: currentPriceListName !== props.item.priceListName ? props.item.priceListName : '',
                                                    })}
                                                    type="button"
                                                    style={{
                                                        all: 'unset',
                                                        cursor: 'pointer',
                                                        textDecoration: 'line-through',
                                                    }}
                                                >
                                                    {defaultRender(props)}
                                                </button>
                                            </div>
                                        )
                                    return defaultRender(props)
                                }}
                            />
                            {!items.filter(x => x.documentTypeId === 2)?.length && [Status.RESOLVED, Status.REJECTED].includes(status)
                                && <Text styles={{ root: { fontStyle: 'italic', marginLeft: '1em' } }}>{this.tKey('noResultFound')}</Text>}
                        </Card>
                        <br />
                        {me.companyId !== ECompany.JetFreeze && (
                            <Card
                                title={this.tKey('documents')}
                                iconName="TextDocument"
                            >
                                <ShimmeredDetailsList
                                    items={items.filter(x => x.documentTypeId !== 2)}
                                    columns={documentColumns}
                                    selectionMode={SelectionMode.none}
                                    onShouldVirtualize={() => true}
                                    enableShimmer={status === Status.PENDING}
                                    layoutMode={DetailsListLayoutMode.justified}
                                    constrainMode={ConstrainMode.unconstrained}
                                    onRenderDetailsHeader={(props, defaultRender) => defaultRender({ ...props, styles: { root: { paddingTop: 0 } } })}
                                    // eslint-disable-next-line react/no-unstable-nested-components
                                    onRenderRow={(props, defaultRender) => {
                                        const item = iniItems.find(x => x.documentId === props.item.documentId)
                                        if (!item)
                                            return defaultRender(props)

                                        if (item.file?.fileId)
                                            return (
                                                <button
                                                    onClick={() => this.downloadFile(item.file)}
                                                    type="button"
                                                    style={{
                                                        all: 'unset',
                                                        cursor: props.item.fileId ? 'pointer' : undefined,
                                                        textDecoration: item.startDate > new Date() || item.endDate < new Date() ? 'line-through' : undefined,
                                                    }}
                                                >
                                                    {defaultRender(props)}
                                                </button>
                                            )
                                        if (item.url)
                                            return (
                                                <a
                                                    href={item.url}
                                                    target="_blank"
                                                    style={{
                                                        all: 'unset',
                                                        cursor: props.item.fileId ? 'pointer' : undefined,
                                                        textDecoration: item.startDate > new Date() || item.endDate < new Date() ? 'line-through' : undefined,
                                                    }}
                                                    rel="noreferrer"
                                                >
                                                    {defaultRender(props)}
                                                </a>
                                            )
                                        return defaultRender(props)
                                    }}
                                />
                                {!items.filter(x => x.documentTypeId !== 2)?.length && [Status.RESOLVED, Status.REJECTED].includes(status)
                                    && <Text styles={{ root: { fontStyle: 'italic', marginLeft: '1em' } }}>{this.tKey('noResultFound')}</Text>}
                            </Card>
                        )}
                    </>
                )}
                {userProfilesWithSuperAdmin.includes(me?.profileId) && (
                    <Card>
                        <ShimmeredDetailsList
                            items={items}
                            columns={documentColumns}
                            selectionMode={SelectionMode.none}
                            onShouldVirtualize={() => true}
                            enableShimmer={status === Status.PENDING}
                            layoutMode={DetailsListLayoutMode.justified}
                            constrainMode={ConstrainMode.unconstrained}
                            onRenderDetailsHeader={(props, defaultRender) => defaultRender({ ...props, styles: { root: { paddingTop: 0 } } })}
                            // eslint-disable-next-line react/no-unstable-nested-components
                            onRenderRow={(props, defaultRender) => (
                                <Link to={`/documents/${props.item?.documentId}`}>
                                    {defaultRender(props)}
                                </Link>
                            )}
                        />
                        {!items?.length && [Status.RESOLVED, Status.REJECTED].includes(status)
                            && <Text styles={{ root: { fontStyle: 'italic', marginLeft: '1em' } }}>{this.tKey('noResultFound')}</Text>}
                    </Card>
                )}
                <Pagination
                    data={pagination}
                    lang={lang}
                    onPageChange={page => this.setState(
                        { pagination: { ...pagination, currentPage: page } },
                        () => this.search(),
                    )}
                    itemName="document"
                />
            </main>
        )
    }
}

IndexDocuments.prototype.flattenObj = flattenObj
IndexDocuments.prototype.handleRenderColumn = handleRenderColumn
IndexDocuments.prototype.tKey = tKey
IndexDocuments.prototype.tObj = tObj
IndexDocuments.prototype.time = time
IndexDocuments.prototype.isValidDate = isValidDate
IndexDocuments.prototype.parseJson = parseJson
IndexDocuments.prototype.getUpdatedList = getUpdatedList
